import apiService from './api';

class SalesService {
  constructor() {
    // In-memory cache for bulk data
    this.cache = {
      bulkData: null,
      lastFetch: null,
      filters: null
    };
    
    // Cache duration: 30 minutes (matching backend)
    this.cacheDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
  }

  // =============================================================================
  // MAIN BULK DATA ENDPOINT - Single API Call
  // =============================================================================
  
  async getBulkSalesData(filters = {}) {
    
    const cacheKey = JSON.stringify(filters);
    const now = Date.now();
  
    // Check if we have valid cached data
    if (this.cache.bulkData && 
        this.cache.lastFetch && 
        this.cache.filters === cacheKey &&
        (now - this.cache.lastFetch) < this.cacheDuration) {
      return this.cache.bulkData;
    }
    
    try {
      const params = new URLSearchParams(filters).toString();
      const url = `/sales/bulk-data${params ? `?${params}` : ''}`;
      
      const response = await apiService.get(url);
      
      // Extract data from the response structure with multiple fallbacks
      let bulkData = null;
      
      if (response.data?.data) {
        bulkData = response.data.data;
      } else if (response.data) {
        bulkData = response.data;
      } else {
        bulkData = response;
      }
      
      // Validate the structure we expect
      if (!bulkData.raw_data) {
        console.warn('⚠️ WARNING: No raw_data property found in response');
      }
      
      if (!Array.isArray(bulkData.raw_data)) {
        console.warn('⚠️ WARNING: raw_data is not an array');
      }
      
      if (bulkData.raw_data && bulkData.raw_data.length === 0) {
        console.warn('⚠️ WARNING: raw_data array is empty');
      }
      
      // Cache the response
      this.cache.bulkData = bulkData;
      this.cache.lastFetch = now;
      this.cache.filters = cacheKey;
      
      return bulkData;
      
    } catch (error) {
      console.error('❌ DETAILED ERROR in getBulkSalesData:');
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response statusText:', error.response.statusText);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request made but no response:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
      
      if (error.config) {
        console.error('Request config:', {
          url: error.config.url,
          method: error.config.method,
          baseURL: error.config.baseURL,
          timeout: error.config.timeout
        });
      }
      
      throw error;
    }
  }

  // =============================================================================
  // CLIENT-SIDE FILTERING METHODS - Updated for raw data structure
  // =============================================================================
  
  /**
   * Filter raw sales data by closer
   */
  filterRawDataByCloser(rawData, closer = 'all') {
    if (closer === 'all') return rawData;
    
    return rawData.filter(record => 
      record.opportunity_closer === closer ||
      record.closer === closer
    );
  }
  
  /**
   * Filter raw sales data by offer type
   */
  filterRawDataByOfferType(rawData, offerType = 'all') {
    if (offerType === 'all') return rawData;
    
    return rawData.filter(record => 
      record.offers_type === offerType && record.offers_sent === true
    );
  }
  
  /**
   * Calculate team performance from raw data
   */
  calculateTeamPerformance(rawData, closer = 'all') {
    const filteredData = this.filterRawDataByCloser(rawData, closer);
    
    // Group by closer
    const closerStats = {};
    
    filteredData.forEach(record => {
      const closerName = record.opportunity_closer || 'Unknown';
      
      if (!closerStats[closerName]) {
        closerStats[closerName] = {
          name: closerName,
          calls: 0,
          appointments: 0,
          deals: 0,
          deal_value: 0,
          showed: 0,
          no_show: 0,
          canceled: 0,
          confirmed: 0
        };
      }
      
      const stats = closerStats[closerName];
      stats.calls += 1;
      stats.appointments += 1;
      
      // Count deals
      if (record.deal_is_won === true) {
        stats.deals += 1;
        stats.deal_value += record.won_revenue || 0;
      }
      
      // Count status categories
      const statusCategory = record.status_category;
      if (statusCategory === 'showed') stats.showed += 1;
      else if (statusCategory === 'no_show') stats.no_show += 1;
      else if (statusCategory === 'canceled') stats.canceled += 1;
      else if (statusCategory === 'confirmed') stats.confirmed += 1;
    });
    
    // Convert to array and sort by deals
    return Object.values(closerStats).sort((a, b) => b.deals - a.deals);
  }
  
  /**
   * Calculate call status from raw data
   */
  calculateCallStatus(rawData, closer = 'all') {
    const filteredData = this.filterRawDataByCloser(rawData, closer);
    
    const statusCounts = {
      showed: 0,
      no_show: 0,
      canceled: 0,
      confirmed: 0,
      total: filteredData.length,
      closer: closer
    };
    
    filteredData.forEach(record => {
      const statusCategory = record.status_category;
      if (statusCategory in statusCounts) {
        statusCounts[statusCategory] += 1;
      }
    });
    
    return statusCounts;
  }
  
  /**
   * Calculate offers data from raw data
   */
  calculateOffers(rawData, closer = 'all', offerType = 'all') {
    let filteredData = this.filterRawDataByCloser(rawData, closer);
    
    // Count offers by type
    const offersByType = {};
    let totalOffers = 0;
    
    filteredData.forEach(record => {
      if (record.offers_sent === true) {
        const type = record.offers_type || 'Unknown';
        offersByType[type] = (offersByType[type] || 0) + 1;
        totalOffers += 1;
      }
    });
    
    // If filtering by specific offer type
    if (offerType !== 'all') {
      totalOffers = offersByType[offerType] || 0;
      const filteredOffersByType = {};
      if (totalOffers > 0) {
        filteredOffersByType[offerType] = totalOffers;
      }
      return {
        total_sent: totalOffers,
        high_probability: Math.round(totalOffers * 0.3),
        medium_probability: Math.round(totalOffers * 0.5),
        low_probability: Math.round(totalOffers * 0.2),
        by_type: filteredOffersByType
      };
    }
    
    return {
      total_sent: totalOffers,
      high_probability: Math.round(totalOffers * 0.3),
      medium_probability: Math.round(totalOffers * 0.5),
      low_probability: Math.round(totalOffers * 0.2),
      by_type: offersByType
    };
  }
  
  /**
   * Calculate KPIs from raw data
   */
  calculateKPIs(rawData, filters = {}) {
    const { closer = 'all', offer_type = 'all' } = filters;
    
    const filteredData = this.filterRawDataByCloser(rawData, closer);
    const callStatus = this.calculateCallStatus(rawData, closer);
    const offers = this.calculateOffers(rawData, closer, offer_type);
    
    const totalCalls = filteredData.length;
    const totalShowed = callStatus.showed;
    const totalDeals = filteredData.filter(r => r.deal_is_won === true).length;
    const totalOpportunityValue = filteredData.reduce((sum, r) => sum + (r.opportunity_value_clean || 0), 0);

    return {
      total_calls: totalCalls,
      show_rate: totalCalls > 0 ? Math.round((totalShowed / totalCalls) * 100 * 10) / 10 : 0,
      appointments: totalCalls,
      deals_offered: offers.total_sent,
      deals_closed: totalDeals,
      opportunity_value: totalOpportunityValue,
      conversion_rate: totalCalls > 0 ? Math.round((totalDeals / totalCalls) * 100 * 10) / 10 : 0
    };
  }
  
  /**
 * Get data freshness information from the database
 */
  async getDataFreshness() {
    try {
      const response = await apiService.get('/sales/data-synced');
      
      return response.data || response;
    } catch (error) {
      console.error('❌ Error fetching data freshness:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive sync status including data age analysis
   */
  async getSyncStatus() {
    try {
      const response = await apiService.get('/sales/sync-status');
      
      return response.data || response;
    } catch (error) {
      console.error('❌ Error fetching sync status:', error);
      throw error;
    }
  }

  /**
   * Check if data needs refresh based on freshness
   */
  async shouldRefreshData(maxAgeMinutes = 60) {
    try {
      const freshness = await this.getDataFreshness();
      
      if (!freshness.data_last_updated) {
        return true; // No timestamp available, should refresh
      }
      
      const dataTimestamp = new Date(freshness.data_last_updated);
      const now = new Date();
      const ageMinutes = (now - dataTimestamp) / (1000 * 60);
      
      
      return ageMinutes > maxAgeMinutes;
    } catch (error) {
      console.error('❌ Error checking data freshness:', error);
      return true; // On error, assume we should refresh
    }
  }
  // =============================================================================
  // MAIN DASHBOARD DATA METHOD - Updated for new structure
  // =============================================================================
  
  async getDashboardData(filters = {}) {
    const bulkData = await this.getBulkSalesData(filters);
    
    // Extract raw data from bulk response
    const rawData = bulkData.raw_data || [];
    
    if (!rawData.length) {
      console.warn('No raw data available');
      return this._getEmptyDashboardData();
    }
    
    const { closer = 'all', offer_type = 'all' } = filters;
    
    // Calculate all metrics from raw data
    const kpis = this.calculateKPIs(rawData, filters);
    const teamData = this.calculateTeamPerformance(rawData, closer);
    const callStatus = this.calculateCallStatus(rawData, closer);
    const offers = this.calculateOffers(rawData, closer, offer_type);
    
    // Extract filter options from metadata
    const filterOptions = bulkData.filter_metadata || {
      available_closers: [],
      available_offer_types: [],
      available_statuses: []
    };
    
    return {
      kpis,
      teamData,
      callStatus,
      offers,
      filterOptions: {
        closers: ['all', ...filterOptions.available_closers],
        offer_types: ['all', ...filterOptions.available_offer_types],
        statuses: ['all', ...filterOptions.available_statuses]
      },
      rawData, // Include raw data for advanced use cases
      summary: bulkData.summary_stats || {},
      lastUpdated: this.cache.lastFetch
    };
  }
  
  // =============================================================================
  // CONVENIENCE METHODS WITH FILTERING
  // =============================================================================
  
  async getFilteredData(filters = {}) {    
    // Handle different filter formats from existing components
    const normalizedFilters = this._normalizeFilters(filters);
    
    const dashboardData = await this.getDashboardData(normalizedFilters);

    return dashboardData;
  }
  
  // =============================================================================
  // LEGACY COMPATIBILITY METHODS - Updated to work with raw data
  // =============================================================================
  
  async getKPIs(filters = {}) {
    const dashboardData = await this.getDashboardData(filters);
    return dashboardData.kpis;
  }

  async getTeamPerformance(filters = {}) {
    const dashboardData = await this.getDashboardData(filters);
    return dashboardData.teamData;
  }

  async getCallStatus(filters = {}) {
    const dashboardData = await this.getDashboardData(filters);
    return dashboardData.callStatus;
  }
  
  async getOffers(filters = {}) {
    const dashboardData = await this.getDashboardData(filters);
    return dashboardData.offers;
  }
  
  // Legacy method that was calling aggregated data
  async getSalesFlow(filters = {}) {
    const dashboardData = await this.getDashboardData(filters);
    
    return {
      booked: dashboardData.callStatus.total,
      showed: dashboardData.callStatus.showed,
      no_show: dashboardData.callStatus.no_show,
      canceled: dashboardData.callStatus.canceled,
      confirmed: dashboardData.callStatus.confirmed,
      unqualified: Math.max(0, dashboardData.callStatus.total - dashboardData.callStatus.showed),
      offers_sent: dashboardData.offers.total_sent,
      deals_closed: dashboardData.kpis.deals_closed,
      total_value: dashboardData.kpis.opportunity_value
    };
  }
  
  // Backward compatibility method
  async getBulkDashboardData(filters = {}) {
    return this.getBulkSalesData(filters);
  }
  
  // =============================================================================
  // CONFIGURATION ENDPOINTS - Updated to use new endpoints
  // =============================================================================
  
  async getAvailableClosers() {
    // Try cache first, fallback to API
    const cachedOptions = this.cache.bulkData?.filter_metadata;
    if (cachedOptions?.available_closers) {
      return { 
        closers: ['all', ...cachedOptions.available_closers], 
        total_closers: cachedOptions.available_closers.length + 1 
      };
    }
    
    return apiService.get('/sales/available-closers');
  }

  async getAvailableOfferTypes() {
    // Try cache first, fallback to API  
    const cachedOptions = this.cache.bulkData?.filter_metadata;
    if (cachedOptions?.available_offer_types) {
      const offerTypes = ['all', ...cachedOptions.available_offer_types].map((type, index) => ({
        id: type === 'all' ? 'all' : type.toLowerCase().replace(/\s+/g, '_'),
        label: type,
        default_percentage: index === 0 ? 1.0 : 1.0 / cachedOptions.available_offer_types.length
      }));
      return { offer_types: offerTypes, total_offer_types: offerTypes.length };
    }
    
    return apiService.get('/sales/available-offer-types');
  }
  
  async getFilterOptions() {
    // Use new combined filter options endpoint
    try {
      return await apiService.get('/sales/filter-options');
    } catch (error) {
      console.error('Error getting filter options:', error);
      // Fallback to cached data
      const cachedOptions = this.cache.bulkData?.filter_metadata;
      if (cachedOptions) {
        return {
          data: {
            closers: ['all', ...cachedOptions.available_closers],
            appointment_statuses: ['all', ...cachedOptions.available_statuses],
            offer_types: ['all', ...cachedOptions.available_offer_types],
            pipeline_stages: ['all', ...cachedOptions.available_pipeline_stages],
            status_categories: ['all', ...cachedOptions.status_categories]
          }
        };
      }
      throw error;
    }
  }
  
  // =============================================================================
  // FIXED HEATMAP ENDPOINTS - Now properly handling backend structure
  // =============================================================================
  
  async getHeatmapData(tabType, filters = {}) {    
    try {
      const params = new URLSearchParams({
        dimension: filters.dimension || 'call_type',
        ...filters
      }).toString();
      
      const response = await apiService.get(`/sales/heatmap/${tabType}${params ? `?${params}` : ''}`);
      
      // SIMPLIFIED: Just extract and return the data
      const heatmapData = response.data || response;
  
      return heatmapData;
      
    } catch (error) {
      console.error('❌ Heatmap API error:', error);
      throw error; // Let the component handle the error
    }
  }
  /**
   * FIXED: Local raw data processing for heatmap fallback
   */
  async _processRawDataForHeatmap(tabType, filters = {}) {    
    try {
      // Get bulk data
      const bulkData = await this.getBulkSalesData({
        start_date: filters.start_date,
        end_date: filters.end_date,
        include_metadata: false
      });
      
      const rawData = bulkData.raw_data || [];
      
      if (!rawData.length) {
        return this._createEmptyHeatmapData(tabType, filters.dimension || 'call_type');
      }
      
      // Filter by closer if specified
      const filteredData = filters.closer && filters.closer !== 'all' 
        ? rawData.filter(record => record.opportunity_closer === filters.closer)
        : rawData;
      
      // Generate time periods
      const periods = this._generateTimePeriods(
        filters.aggregation_level || 'weekly',
        filters.periods_back || 8,
        filters.end_date
      );
      
      // Group data by periods
      const groupedData = this._groupDataByPeriods(filteredData, periods);
      
      // Process based on tab type and dimension
      return this._processDataByTabAndDimension(groupedData, periods, tabType, filters.dimension || 'call_type');
      
    } catch (error) {
      console.error('❌ Error processing raw data for heatmap:', error);
      return this._createEmptyHeatmapData(tabType, filters.dimension || 'call_type');
    }
  }

  /**
   * FIXED: Generate time periods for heatmap
   */
  _generateTimePeriods(aggregationLevel, periodsBack, endDate) {
    const periods = [];
    const today = endDate ? new Date(endDate) : new Date();
    
    for (let i = periodsBack - 1; i >= 0; i--) {
      const period = new Date(today);
      
      if (aggregationLevel === 'weekly') {
        period.setDate(today.getDate() - (i * 7));
        const weekStart = new Date(period);
        weekStart.setDate(period.getDate() - period.getDay());
        const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
        
        periods.push({
          label: `Week ${periodsBack - i}`,
          start: weekStart.toISOString().split('T')[0],
          end: weekEnd.toISOString().split('T')[0]
        });
      } else {
        period.setMonth(today.getMonth() - i);
        const monthStart = new Date(period.getFullYear(), period.getMonth(), 1);
        const monthEnd = new Date(period.getFullYear(), period.getMonth() + 1, 0);
        
        periods.push({
          label: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          start: monthStart.toISOString().split('T')[0],
          end: monthEnd.toISOString().split('T')[0]
        });
      }
    }
    
    return periods;
  }

  /**
   * FIXED: Group data by time periods using correct date field
   */
  _groupDataByPeriods(data, periods) {
    const grouped = {};
    
    periods.forEach(period => {
      grouped[period.label] = data.filter(record => {
        // FIXED: Use appointment_date from computed field
        const recordDate = record.appointment_date;
        if (!recordDate) return false;
        
        return recordDate >= period.start && recordDate <= period.end;
      });
    });
    
    return grouped;
  }

  /**
   * FIXED: Process data by tab type and dimension
   */
  _processDataByTabAndDimension(groupedData, periods, tabType, dimension) {
    const headers = periods.map(p => p.label);
    
    switch (tabType) {
      case 'sales_calls':
        return this._processSalesCallsHeatmap(groupedData, headers, dimension);
      case 'offers_sent':
        return this._processOffersHeatmap(groupedData, headers, dimension);
      case 'closed_deals':
        return this._processDealsHeatmap(groupedData, headers, dimension);
      default:
        return this._createEmptyHeatmapData(tabType, dimension);
    }
  }

  /**
   * FIXED: Process sales calls heatmap
   */
  _processSalesCallsHeatmap(groupedData, headers, dimension) {
    if (dimension === 'call_type') {
      const statusCategories = ['showed', 'no_show', 'canceled', 'confirmed', 'other'];
      const rows = statusCategories.map(status => {
        const cells = headers.map(header => {
          const periodData = groupedData[header] || [];
          const count = periodData.filter(record => record.status_category === status).length;
          return { value: count, display: count.toString() };
        });
        
        return {
          label: status.replace('_', ' ').toUpperCase(),
          cells,
          total: cells.reduce((sum, cell) => sum + cell.value, 0)
        };
      });
      
      return {
        headers,
        rows,
        totals: headers.map(header => {
          const periodData = groupedData[header] || [];
          return periodData.length;
        }),
        dimension: 'call_type',
        tab_type: 'sales_calls'
      };
    } else if (dimension === 'closer') {
      // Get all unique closers
      const allClosers = new Set();
      Object.values(groupedData).forEach(periodData => {
        periodData.forEach(record => {
          if (record.opportunity_closer) {
            allClosers.add(record.opportunity_closer);
          }
        });
      });
      
      const rows = Array.from(allClosers).map(closer => {
        const cells = headers.map(header => {
          const periodData = groupedData[header] || [];
          const count = periodData.filter(record => record.opportunity_closer === closer).length;
          return { value: count, display: count.toString() };
        });
        
        return {
          label: closer,
          cells,
          total: cells.reduce((sum, cell) => sum + cell.value, 0)
        };
      });
      
      return {
        headers,
        rows,
        totals: headers.map(header => {
          const periodData = groupedData[header] || [];
          return periodData.length;
        }),
        dimension: 'closer',
        tab_type: 'sales_calls'
      };
    }
    
    return this._createEmptyHeatmapData('sales_calls', dimension);
  }

  /**
   * FIXED: Process offers heatmap
   */
  _processOffersHeatmap(groupedData, headers, dimension) {
    // Filter to only offers data
    const offersGrouped = {};
    for (const [header, periodData] of Object.entries(groupedData)) {
      offersGrouped[header] = periodData.filter(record => record.offers_sent === true);
    }
    
    if (dimension === 'offer_type') {
      // Get all unique offer types
      const allOfferTypes = new Set();
      Object.values(offersGrouped).forEach(periodData => {
        periodData.forEach(record => {
          if (record.offers_type) {
            allOfferTypes.add(record.offers_type);
          }
        });
      });
      
      const rows = Array.from(allOfferTypes).map(offerType => {
        const cells = headers.map(header => {
          const periodData = offersGrouped[header] || [];
          const count = periodData.filter(record => record.offers_type === offerType).length;
          return { value: count, display: count.toString() };
        });
        
        return {
          label: offerType,
          cells,
          total: cells.reduce((sum, cell) => sum + cell.value, 0)
        };
      });
      
      return {
        headers,
        rows,
        totals: headers.map(header => {
          const periodData = offersGrouped[header] || [];
          return periodData.length;
        }),
        dimension: 'offer_type',
        tab_type: 'offers_sent'
      };
    } else if (dimension === 'probability') {
      const probabilities = ['High', 'Medium', 'Low'];
      const rows = probabilities.map(prob => {
        const cells = headers.map(header => {
          const periodData = offersGrouped[header] || [];
          // Simple probability assignment based on won deals
          let count;
          if (prob === 'High') {
            count = periodData.filter(r => r.deal_is_won === true).length;
          } else if (prob === 'Medium') {
            count = periodData.filter(r => r.deal_is_won === false && r.deal_closed === true).length;
          } else {
            count = periodData.filter(r => !r.deal_closed).length;
          }
          return { value: count, display: count.toString() };
        });
        
        return {
          label: prob,
          cells,
          total: cells.reduce((sum, cell) => sum + cell.value, 0)
        };
      });
      
      return {
        headers,
        rows,
        totals: headers.map(header => {
          const periodData = offersGrouped[header] || [];
          return periodData.length;
        }),
        dimension: 'probability',
        tab_type: 'offers_sent'
      };
    }
    
    return this._createEmptyHeatmapData('offers_sent', dimension);
  }

  /**
   * FIXED: Process deals heatmap
   */
  _processDealsHeatmap(groupedData, headers, dimension) {
    // Filter to only won deals
    const dealsGrouped = {};
    for (const [header, periodData] of Object.entries(groupedData)) {
      dealsGrouped[header] = periodData.filter(record => record.deal_is_won === true);
    }
    
    if (dimension === 'person') {
      // Get all unique closers who closed deals
      const allClosers = new Set();
      Object.values(dealsGrouped).forEach(periodData => {
        periodData.forEach(record => {
          if (record.opportunity_closer) {
            allClosers.add(record.opportunity_closer);
          }
        });
      });
      
      const rows = Array.from(allClosers).map(closer => {
        const cells = headers.map(header => {
          const periodData = dealsGrouped[header] || [];
          const count = periodData.filter(record => record.opportunity_closer === closer).length;
          return { value: count, display: count.toString() };
        });
        
        return {
          label: closer,
          cells,
          total: cells.reduce((sum, cell) => sum + cell.value, 0)
        };
      });
      
      return {
        headers,
        rows,
        totals: headers.map(header => {
          const periodData = dealsGrouped[header] || [];
          return periodData.length;
        }),
        dimension: 'person',
        tab_type: 'closed_deals'
      };
    } else if (dimension === 'offer_type') {
      // Get all unique offer types for closed deals
      const allOfferTypes = new Set();
      Object.values(dealsGrouped).forEach(periodData => {
        periodData.forEach(record => {
          if (record.offers_type) {
            allOfferTypes.add(record.offers_type);
          }
        });
      });
      
      const rows = Array.from(allOfferTypes).map(offerType => {
        const cells = headers.map(header => {
          const periodData = dealsGrouped[header] || [];
          const count = periodData.filter(record => record.offers_type === offerType).length;
          return { value: count, display: count.toString() };
        });
        
        return {
          label: offerType,
          cells,
          total: cells.reduce((sum, cell) => sum + cell.value, 0)
        };
      });
      
      return {
        headers,
        rows,
        totals: headers.map(header => {
          const periodData = dealsGrouped[header] || [];
          return periodData.length;
        }),
        dimension: 'offer_type',
        tab_type: 'closed_deals'
      };
    }
    
    return this._createEmptyHeatmapData('closed_deals', dimension);
  }

  /**
   * Create empty heatmap structure
   */
  _createEmptyHeatmapData(tabType, dimension) {
    return {
      headers: [],
      rows: [],
      totals: [],
      dimension,
      tab_type: tabType,
      empty: true,
      metadata: {
        aggregation_level: 'weekly',
        total_records: 0,
        period_count: 0
      }
    };
  }

  async getHeatmapDimensions(tabType) {
    try {
      const response = await apiService.get(`/sales/heatmap/${tabType}/dimensions`);
      
      // Extract dimensions from response
      let dimensions = null;
      if (response.data?.dimensions) {
        dimensions = response.data.dimensions;
      } else if (response.dimensions) {
        dimensions = response.dimensions;
      } else {
        throw new Error('No dimensions found in response');
      }
      
      return { dimensions };
      
    } catch (error) {
      console.error('❌ Error getting heatmap dimensions:', error);
      
      // Fallback: Return default dimensions
      const defaultDimensions = {
        'sales_calls': [
          { id: 'call_type', label: 'Call Status', description: 'Group by appointment status' },
          { id: 'closer', label: 'Team Member', description: 'Group by closer' }
        ],
        'offers_sent': [
          { id: 'offer_type', label: 'Offer Type', description: 'Group by offer package' },
          { id: 'probability', label: 'Probability', description: 'Group by closing probability' }
        ],
        'closed_deals': [
          { id: 'person', label: 'Team Member', description: 'Group by closer' },
          { id: 'offer_type', label: 'Offer Type', description: 'Group by offer package' }
        ]
      };
      
      return { dimensions: defaultDimensions[tabType] || [] };
    }
  }
  
  // =============================================================================
  // UTILITY METHODS
  // =============================================================================
  
  _normalizeFilters(filters) {
    // Convert various filter formats to standard format
    const normalized = {};
    
    // Handle date filters
    if (filters.start_date) normalized.start_date = filters.start_date;
    if (filters.end_date) normalized.end_date = filters.end_date;
    if (filters.reference_date) normalized.reference_date = filters.reference_date;
    
    // Handle aggregation filters (not used in new API but kept for compatibility)
    if (filters.aggregation_level) normalized.aggregation_level = filters.aggregation_level;
    if (filters.periods_back) normalized.periods_back = filters.periods_back;
    
    // Handle closer filter (various naming conventions)
    const closer = filters.closer || filters.closer_name || filters.assigned_to || 'all';
    if (closer !== 'all') normalized.closer = closer;
    
    // Handle offer type filter
    const offerType = filters.offer_type || filters.offerType || 'all';
    if (offerType !== 'all') normalized.offer_type = offerType;
    
    return normalized;
  }
  
  _getEmptyDashboardData() {
    return {
      kpis: this._getEmptyKPIs(),
      teamData: [],
      callStatus: { showed: 0, no_show: 0, canceled: 0, confirmed: 0, total: 0, closer: 'all' },
      offers: { total_sent: 0, high_probability: 0, medium_probability: 0, low_probability: 0, by_type: {} },
      filterOptions: { closers: ['all'], offer_types: ['all'], statuses: ['all'] },
      rawData: [],
      summary: {},
      lastUpdated: null
    };
  }
  
  _getEmptyKPIs() {
    return {
      total_calls: 0,
      show_rate: 0.0,
      appointments: 0,
      deals_offered: 0,
      deals_closed: 0,
      opportunity_value: 0.0,
      conversion_rate: 0.0
    };
  }
  
  // =============================================================================
  // CACHE MANAGEMENT
  // =============================================================================
  
  clearCache() {
    this.cache = {
      bulkData: null,
      lastFetch: null,
      filters: null
    };
  }
  
  isCacheValid() {
    const now = Date.now();
    return this.cache.bulkData && 
           this.cache.lastFetch && 
           (now - this.cache.lastFetch) < this.cacheDuration;
  }
  
  getCacheInfo() {
    return {
      hasCachedData: !!this.cache.bulkData,
      lastFetch: this.cache.lastFetch,
      cacheAge: this.cache.lastFetch ? Date.now() - this.cache.lastFetch : null,
      isValid: this.isCacheValid(),
      totalRecords: this.cache.bulkData?.total_records || 0,
      dateRange: this.cache.bulkData?.date_range || null
    };
  }
  
  // =============================================================================
  // HEALTH CHECK
  // =============================================================================
  
  async checkHealth() {
    return apiService.get('/sales/health');
  }

  async isServiceHealthy() {
    try {
      const health = await this.checkHealth();
      return health && health.status === 'healthy';
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}

const salesServiceInstance = new SalesService();
export default salesServiceInstance;